/** @jsx jsx */
import { useState, useContext, Fragment, useEffect } from 'react'
import { css, jsx, keyframes } from '@emotion/core'
import {
  Modal,
  Switch,
  Button,
  ActionModal,
  notificationConnect,
} from '@livechat/design-system'
import useMedia from 'use-media'

import { Context } from '../context'
import api from '../api'
import openIcon from '../assets/icons/open.svg'
import SurveyDetails from './SurveyDetails'

const showModalAnimation = keyframes`
from {
 transform: translateY(20px);
}
`

const dropAnimation = keyframes`
to {
 transform: scale(0.3);
 opacity: 0;
}
`

const boxCss = (drop, active) => css`
  width: 240px;
  height: 150px;
  border: 1px solid transparent;
  box-shadow: rgb(228 228 228) 0px 1px 5px 0px;
  border-radius: 10px;
  margin-top: 20px;
  margin-left: 10px;
  margin-right: 10px;
  cursor: pointer;
  transition: all 0.1s ease-in-out;
  background-color: white;
  position: relative;

  :hover {
    border-color: #4384f5;
  }

  ${drop &&
  css`
    animation: ${dropAnimation} 0.2s ease forwards;
  `};

  .body {
    width: 100%;
    height: 110px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }

  .name {
    opacity: ${active ? 1 : 0.5};
    margin: 0 20px 15px 20px;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 300;

    .count {
      opacity: ${active ? 0.5 : 1};
    }
  }

  .footer {
    width: 100%;
    height: calc(100% - 111px);
    display: flex;
    align-items: center;
    justify-content: flex-end;
    border-top: 1px solid #d8dee5;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
    transition: all 0.2s ease-in-out;
    background-color: white !important;

    .lc-switch {
      margin-right: 10px;
      margin-left: 10px;
    }
  }

  .drop-btn {
    border-color: #d64646;
    color: #d64646;

    > img {
      margin-right: 5px;
    }
  }

  .show-results {
    width: 130px;
  }

  .copy-link {
    width: 130px;
    margin-right: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #3f8cff;
    cursor: pointer;
    display: flex;
    align-items: center;

    .copy-icon {
      margin-bottom: -4px;
    }

    .copy-label {
      text-decoration: none !important;
    }
  }

  .lc-modal-base {
    cursor: default !important;
    min-width: 300px !important;
  }

  .lc-modal__body {
    max-height: calc(100% - 128px);
  }
`

const labelsCss = css`
  position: absolute;
  right: -12px;
  top: -12px;
  width: 100%;
  display: flex;
  justify-content: flex-end;
`

const labelCss = css`
  color: #4384f5;
  background-color: #e9f0ff;
  border-radius: 20px;
  padding: 4px 8px;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: 0.5px;
  margin-right: 5px;
  max-width: 70px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const linkCss = css`
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Survey = ({
  survey,
  id,
  name,
  count,
  active,
  structure,
  handleActive,
  handleDrop,
  isRemoved,
  publicToken,
}) => {
  const { surveyToken, setSurveyFeedback, setView, VIEWS, setFeedbackType } =
    useContext(Context)
  const [isContentOpen, setIsContentOpen] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const isSmall = useMedia({ maxWidth: 375 })

  const [currPublicToken, setCurrPublicToken] = useState(publicToken)

  useEffect(() => {
    const request = async () => {
      const token = await api.createPublicToken(surveyToken, id)
      setCurrPublicToken(token)
    }

    if (!publicToken) {
      request()
    }
  }, [])

  const renderWithLink = (children) => {
    // INFO: redner link with publicSurveyId if exists. Old surveys couldn't have it
    if (survey.publicSurveyId) {
      return (
        <a
          href={`${process.env.REACT_APP_SURVEYS_SHORT}/public/${survey.publicSurveyId}`}
          target="_blank"
          rel="noopener noreferrer"
          css={linkCss}
        >
          {children}
        </a>
      )
    }

    if (currPublicToken) {
      return (
        <a
          href={`${process.env.REACT_APP_SURVEYS}/chat-widget/${currPublicToken}`}
          target="_blank"
          rel="noopener noreferrer"
          css={linkCss}
        >
          {children}
        </a>
      )
    }

    return children
  }

  return (
    <div css={boxCss(isRemoved, active)} data-cy="surveys-survey-card">
      <div css={labelsCss}>
        {survey?.grouping_labels?.length > 2 ? (
          <Fragment>
            <span css={labelCss}>{survey?.grouping_labels[0]}</span>
            <span css={labelCss}>{survey?.grouping_labels[1]}</span>
            <span css={labelCss}>
              +{survey?.grouping_labels?.length - 2} more
            </span>
          </Fragment>
        ) : (
          survey?.grouping_labels?.map((l, index) => (
            <span key={`label-${l}-${index}`} css={labelCss}>
              {l}
            </span>
          ))
        )}
      </div>
      <div className="body" onClick={() => setIsContentOpen(true)}>
        <span className="name" data-cy="surveys-survey-card-name">
          {name} <span className="count">({count || 0})</span>
        </span>
      </div>
      <div className="footer" onClick={() => setIsContentOpen(true)}>
        <span
          css={css`
            font-size: 15px;
            line-height: 22px;
            font-weight: 300;
          `}
        >
          Survey is {active ? 'active' : 'inactive'}
        </span>
        <Switch
          size="compact"
          onChange={() => {
            handleActive()
          }}
          on={active || false}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      {isContentOpen && (
        <Modal
          onClose={() => setIsContentOpen(false)}
          css={css`
            width: 1000px;
            height: 90%;
            animation: ${showModalAnimation} 0.3s ease;

            max-width: 1000px;
          `}
          heading={
            <span
              data-cy="surveys-survey-details-name"
              css={css`
                display: flex;
                align-items: center;

                .open-survey {
                  margin-left: 5px;
                  width: 20px;
                  cursor: pointer;
                }
              `}
            >
              <span>{name}</span>
              {renderWithLink(
                <img src={openIcon} alt="Open survey" className="open-survey" />
              )}
              {survey?.grouping_labels?.length > 0 && (
                <div
                  css={css`
                    margin-top: -5px;
                    margin-left: 5px;
                  `}
                >
                  {survey?.grouping_labels.map((label, index) => (
                    <span
                      key={`survey-title-label-${label}-${index}`}
                      css={labelCss}
                    >
                      {label}
                    </span>
                  ))}
                </div>
              )}
            </span>
          }
          footer={
            <Fragment>
              {isDeleteOpen && (
                <ActionModal
                  onClose={() => setIsDeleteOpen(false)}
                  style={{ width: '410px' }}
                  heading="Danger!"
                  actions={
                    <Fragment>
                      <Button
                        size="large"
                        onClick={() => setIsDeleteOpen(false)}
                        secondary
                        style={{ marginRight: '15px' }}
                      >
                        {isSmall ? 'Back' : 'Wait, go back'}
                      </Button>
                      <Button
                        className="delete-btn"
                        size="large"
                        loading={deleteLoading}
                        onClick={async () => {
                          setDeleteLoading(true)
                          await handleDrop()
                          setDeleteLoading(false)
                          setIsContentOpen(false)
                        }}
                        destructive
                      >
                        {isSmall ? 'Delete' : 'Yes, delete'}
                      </Button>
                    </Fragment>
                  }
                >
                  You’re about to do something that cannot be undone. Are you
                  sure you want to continue?
                </ActionModal>
              )}
              <Button
                type="ghost"
                className="drop-btn"
                onClick={() => setIsDeleteOpen(true)}
              >
                {isSmall ? 'Delete' : 'Delete survey'}
              </Button>
              <Button
                type="ghost"
                onClick={() => setIsEditOpen(!isEditOpen)}
                css={css`
                  margin-right: auto;
                  margin-left: 15px;
                `}
              >
                {isEditOpen
                  ? 'Close editing'
                  : isSmall
                  ? 'Edit'
                  : 'Edit survey'}
              </Button>
              {renderWithLink(
                <Button
                  className="copy-link"
                  loaderLabel="Preparing link.."
                  loading={!currPublicToken}
                  css={css`
                    width: auto !important;
                  `}
                >
                  <span className="copy-label">Browse preview</span>
                </Button>
              )}
              <Button
                primary
                className="show-results"
                onClick={() => {
                  setSurveyFeedback(id)
                  setFeedbackType('summary')
                  setView(VIEWS.FEEDBACK)
                }}
              >
                Results
              </Button>
            </Fragment>
          }
        >
          <SurveyDetails
            survey={survey}
            id={id}
            name={name}
            count={count}
            active={active}
            structure={structure}
            handleActive={handleActive}
            loading={deleteLoading}
            isEditOpen={isEditOpen}
          />
        </Modal>
      )}
    </div>
  )
}

export default notificationConnect(Survey)
